const SIZE_OPTION_NAMES = ['Size', 'サイズ', 'size'];
const SIZE_DISPLAY_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', '3L', '4L', '5L', 'Free', 'フリー'];
const DRAWER_TRANSITION_MS = 720;
const TOAST_TRANSITION_MS = 320;

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.drawerController = window.NaoDrawerShell.create(this, {
      activeClass: 'active',
      animateClass: 'animate',
      outsideClickIgnoreSelector: '.cart-drawer__add-toast, .cart-drawer__size-picker',
      overlaySelector: '#CartDrawer-Overlay',
      panelSelector: '.drawer__inner',
    });
    this.drawerController.connect();
    this.setHeaderCartIconAccessibility();
    this.bindFindSizeButton();
    this.bindAddToCartToast();
  }

  bindAddToCartToast() {
    this.querySelector('.cart-drawer__add-toast-close')?.addEventListener('click', () => {
      this.closeAddToCartToast();
    });
  }

  bindFindSizeButton() {
    this.querySelector('.cart-drawer__size-picker-find-size')?.addEventListener('click', (event) => {
      event.preventDefault();
      this.openSizeChart();
    });
  }

  openSizeChart() {
    const sectionId = this.sizePickerSectionId;
    if (!sectionId) return;

    const templateId = `ProductDetailDrawerSizeChart-${sectionId}`;
    const trigger = document.querySelector(`[data-product-detail-drawer-target="${templateId}"]`);
    if (!(trigger instanceof HTMLElement)) return;

    trigger.click();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.drawerController.open(triggeredBy, {
      focusContainer: this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer'),
      focusElement: this.querySelector('.drawer__inner') || this.querySelector('.drawer__close'),
    });
  }

  close(closeOptions = {}) {
    if (this.classList.contains('size-picker-mode')) {
      this.closeSizePicker(true);
      return;
    }

    this.drawerController.close(closeOptions);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const sectionHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      if (!sectionHTML) return;

      sectionElement.innerHTML = sectionHTML;
    });

    this.classList.remove('size-picker-mode');
    this.classList.remove('size-picker-host');
    this.resetDrawerContentVisibility();

    if (this.showToastAfterAdd) {
      this.showToastAfterAdd = false;
      this.clearSizePickerCloseTimer();
      this.showAddToCartToast(parsedState);
      this.drawerController.close({ restoreFocus: false });
      return;
    }

    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.drawerController.setActiveElement(element);
  }

  getDrawerContentElements() {
    return {
      sizePicker: this.querySelector('.cart-drawer__size-picker'),
      header: this.querySelector('.drawer__header'),
      items: this.querySelector('cart-drawer-items'),
      footer: this.querySelector('.drawer__footer'),
      empty: this.querySelector('.drawer__inner-empty'),
    };
  }

  resetDrawerContentVisibility() {
    const { sizePicker, header, items, footer, empty } = this.getDrawerContentElements();

    if (sizePicker) sizePicker.hidden = true;
    if (header) header.hidden = false;
    if (items) items.hidden = false;
    if (footer) footer.hidden = false;
    if (empty) empty.hidden = false;
  }

  setDrawerContentHidden(hidden) {
    const { header, items, footer, empty } = this.getDrawerContentElements();

    if (header) header.hidden = hidden;
    if (items) items.hidden = hidden;
    if (footer) footer.hidden = hidden;
    if (empty) empty.hidden = hidden;
  }

  showEmptyDrawerContent() {
    const { header, items, footer, empty } = this.getDrawerContentElements();

    if (empty) empty.hidden = false;
    if (header) header.hidden = false;
    if (items) items.hidden = true;
    if (footer) footer.hidden = true;
  }

  openSizePicker(productData, sectionId, productForm) {
    this.sizePickerProductData = productData;
    this.sizePickerSectionId = sectionId;
    this.sizePickerProductForm = productForm;

    this.buildSizeGrid(productData);

    const { sizePicker } = this.getDrawerContentElements();

    this.classList.add('size-picker-host');

    if (sizePicker) sizePicker.hidden = false;
    this.setDrawerContentHidden(true);

    if (!this.classList.contains('active')) {
      this.open();
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('size-picker-mode');
      });
    });
  }

  clearSizePickerCloseTimer() {
    if (!this.sizePickerCloseTimer) return;

    window.clearTimeout(this.sizePickerCloseTimer);
    this.sizePickerCloseTimer = null;
  }

  closeSizePicker(closeDrawer = false) {
    const { sizePicker } = this.getDrawerContentElements();

    this.clearSizePickerCloseTimer();
    this.classList.remove('size-picker-mode');

    this.sizePickerCloseTimer = window.setTimeout(() => {
      this.sizePickerCloseTimer = null;

      if (sizePicker) sizePicker.hidden = true;

      if (closeDrawer) {
        this.close();
        window.setTimeout(() => {
          this.classList.remove('size-picker-host');
        }, DRAWER_TRANSITION_MS);
        return;
      }

      this.classList.remove('size-picker-host');
      this.resetDrawerContentVisibility();

      if (this.classList.contains('is-empty')) {
        this.showEmptyDrawerContent();
      }
    }, DRAWER_TRANSITION_MS);
  }

  showAddToCartToast(item) {
    const toast = this.querySelector('.cart-drawer__add-toast');
    if (!toast) return;

    const message = toast.querySelector('[data-cart-toast-message]');
    const productTitle = item.product_title || item.title || '';
    const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? ` ${item.variant_title}` : '';
    const title = `${productTitle}${variantTitle}`.trim();
    const template = toast.dataset.messageTemplate || '[title]';

    if (message) {
      message.textContent = template.replace('[title]', title);
    }

    if (!this.toastHome) {
      this.toastHome = toast.parentElement;
    }

    if (toast.parentElement !== document.body) {
      document.body.appendChild(toast);
    }

    this.activeToast = toast;

    this.classList.remove('size-picker-mode');
    this.classList.remove('size-picker-host');
    this.classList.add('toast-visible');
    toast.hidden = false;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        toast.classList.add('is-visible');
      });
    });
  }

  closeAddToCartToast() {
    const toast = this.activeToast || this.querySelector('.cart-drawer__add-toast');
    if (!toast) return;

    toast.classList.remove('is-visible');
    window.setTimeout(() => {
      toast.hidden = true;
      this.classList.remove('toast-visible');

      if (this.toastHome && toast.parentElement === document.body) {
        this.toastHome.appendChild(toast);
      }

      this.activeToast = null;
    }, TOAST_TRANSITION_MS);
  }

  buildSizeGrid(productData) {
    const grid = this.querySelector('.cart-drawer__size-picker-grid');
    if (!grid) return;

    const sizeOptionIndex = this.getSizeOptionIndex(productData);

    if (sizeOptionIndex === -1) return;

    grid.innerHTML = this.getSortedSizeValues(productData, sizeOptionIndex)
      .map((value) => this.renderSizeOption(value, productData, sizeOptionIndex))
      .join('');
  }

  getSizeOptionIndex(productData) {
    return productData.options.findIndex((optionName) => SIZE_OPTION_NAMES.includes(optionName));
  }

  getSortedSizeValues(productData, sizeOptionIndex) {
    const values = [...new Set(productData.variants.map((variant) => variant.options[sizeOptionIndex]))];
    const orderedValues = SIZE_DISPLAY_ORDER.filter((size) => values.includes(size));
    const remainingValues = values.filter((size) => !SIZE_DISPLAY_ORDER.includes(size));

    return [...orderedValues, ...remainingValues];
  }

  renderSizeOption(value, productData, sizeOptionIndex) {
    const variant = productData.variants.find((item) => item.options[sizeOptionIndex] === value);
    if (!variant?.available) {
      return `
        <div class="cart-drawer__size-picker-item">
          <div class="cart-drawer__size-picker-unavailable">
            <span class="cart-drawer__size-picker-text">${value}</span>
          </div>
        </div>
      `;
    }

    return `
      <div class="cart-drawer__size-picker-item">
        <button
          type="button"
          class="cart-drawer__size-picker-btn"
          data-variant-id="${variant.id}"
          data-size-value="${value}"
          onclick="this.closest('cart-drawer').selectSize(this)"
        >
          ${value}
        </button>
      </div>
    `;
  }

  selectSize(button) {
    const variantId = button.dataset.variantId;
    const sizeValue = button.dataset.sizeValue;
    const form = this.sizePickerProductForm;
    if (form) {
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = variantId;
        variantInput.disabled = false;
      }

      form.dataset.sizeSelected = 'true';
    }

    this.syncVariantSelectors(sizeValue);

    this.showToastAfterAdd = true;
    this.closeSizePicker(true);

    if (form) {
      const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
      form.dispatchEvent(submitEvent);
    }
  }

  syncVariantSelectors(sizeValue) {
    const variantSelects = document.getElementById(`variant-selects-${this.sizePickerSectionId}`);
    if (!variantSelects) return;

    variantSelects.querySelectorAll('select').forEach((select) => {
      const matchingOption = Array.from(select.options).find((option) => option.value === sizeValue);
      if (!matchingOption) return;

      select.value = sizeValue;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    });

    variantSelects.querySelectorAll('fieldset').forEach((fieldset) => {
      const legend = fieldset.querySelector('legend');
      if (!legend || !SIZE_OPTION_NAMES.some((name) => legend.textContent.includes(name))) return;

      fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
        if (input.value !== sizeValue) return;

        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
